/* eslint-disable react-hooks/exhaustive-deps */
import firebase_app from '@/lib/firebase/configs';
import dayjs from 'dayjs';
import { getAnalytics, setUserId, setUserProperties } from 'firebase/analytics';
import { getAuth, onAuthStateChanged, reload } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, useContext, useEffect, useState } from 'react';
import { useDateContext } from './DateContext';

const auth = getAuth(firebase_app);
export const AuthContext = createContext({});
export const useAuthContext = () => useContext(AuthContext);

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const { date } = useDateContext();
  const [bookmarks, setBookmarks] = useState([]);
  const [userDocument, setUserDocument] = useState(null);
  const [userAdditionalInfo, setUserAdditionalInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDocumentLoading, setUserDocumentLoading] = useState(true);
  const [userDocumentLoaded, setUserDocumentLoaded] = useState(false);
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const router = useRouter();

  useEffect(() => {
    (async () => {
      if (user) {
        if (searchParams.get('reload-user')) {
          await reload(user);
        }
        if (user?.emailVerified == false) {
          router.push('/verify-email');
        }
      }
    })();
  }, [pathName]);

  const isBookmark = (jobId) => {
    return bookmarks?.findIndex((item) => item.id == jobId) >= 0;
  };

  useEffect(() => {
    if (user != null) {
      requestPermission();
      const db = getFirestore(firebase_app);
      setUserDocumentLoading(true);
      const unsubscribe = onSnapshot(
        doc(db, 'Users', user.uid),
        { includeMetadataChanges: true },
        (doc) => {
          setUserDocument((prev) => {
            return { ...doc.data(), cvs: prev && prev?.cvs ? prev?.cvs : [] };
          });
          const analytics = getAnalytics(firebase_app);
          setUserId(analytics, user.uid);
          setUserProperties(analytics, {
            role: doc.data()?.role ?? 'visitor',
          });
          setUserDocumentLoading(false);
          setUserDocumentLoaded(true);
        },
        () => {
          setUserDocumentLoading(false);
          setUserDocumentLoaded(false);
        }
      );
      return () => unsubscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user != null) {
      const db = getFirestore(firebase_app);
      const userBookmarksRef = collection(db, 'Users', user.uid, 'FavoriteJobs');
      const unsubscribe = onSnapshot(
        userBookmarksRef,
        { includeMetadataChanges: true },
        (snapShot) => {
          const bookmarksTemp = [];
          snapShot.forEach((item) => {
            bookmarksTemp.push({ bookmarkId: item.id, ...item.data() });
          });
          setBookmarks(bookmarksTemp);
        }
      );

      return () => unsubscribe();
    } else {
      setBookmarks([]);
    }
  }, [user]);

  async function requestPermission() {
    try {
      const db = getFirestore(firebase_app);
      const permission = await Notification?.requestPermission();

      if (permission === 'granted') {
        const messaging = getMessaging(firebase_app);
        getToken(messaging, {
          vapidKey: process.env.FCM_API_KEY,
        }).then(async (currentToken) => {
          if (currentToken) {
            const fcmDocument = await getDoc(doc(db, 'FCMTokens', user.uid));
            if (fcmDocument.exists()) {
              let fcmData = fcmDocument.data();
              const tokenExists =
                fcmData.tokens.findIndex((item) => item.token == currentToken) >= 0;
              if (!tokenExists) {
                setDoc(doc(db, 'FCMTokens', user.uid), {
                  tokens: [...fcmData?.tokens, { token: currentToken, timeStamp: new Date() }],
                });
              }
            } else {
              setDoc(doc(db, 'FCMTokens', user.uid), {
                tokens: [{ token: currentToken, timeStamp: new Date() }],
              }).catch((e) => {});
            }
          }
        });
      }
    } catch (e) {}
  }

  const removeToken = async () => {
    const messaging = getMessaging(firebase_app);
    const db = getFirestore(firebase_app);
    deleteToken(messaging).catch((e) => {});
    return getToken(messaging, {
      vapidKey: process.env.FCM_API_KEY,
    })
      .then(async (currentToken) => {
        if (currentToken) {
          const fcmDocument = await getDoc(doc(db, 'FCMTokens', user.uid));
          if (fcmDocument.exists()) {
            let fcmData = fcmDocument.data();
            setDoc(doc(db, 'FCMTokens', user.uid), {
              tokens: fcmData?.tokens.filter((item) => item.token != currentToken),
            });
          }
        }
      })
      .catch((err) => {});
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        if (user?.emailVerified == false) {
          router.push('/verify-email');
        }
      } else {
        setUser(null);
        setUserDocument(null);
        setUserAdditionalInfo(null);
        setUserDocumentLoading(false);
        setUserDocumentLoaded(false);

        const analytics = getAnalytics(firebase_app);
        setUserProperties(analytics, {
          role: 'visitor',
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user != null) {
      const currentDate = dayjs(date).format('DD MMMM YYYY');
      const db = getFirestore(firebase_app);
      const q = query(
        collection(db, 'Users', user.uid, 'CvCounter'),
        where('date', '==', currentDate)
      );

      const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snapShot) => {
        const cvCounter = snapShot.docs.map((doc) => {
          return doc.data();
        });
        setUserDocument((prevData) => {
          const mergedData = { ...prevData, cvCounter: cvCounter[0] }; // Assuming `newData` is the variable containing your new data
          return mergedData;
        });
      });
      return () => unsubscribe();
    } else {
    }
  }, [user]);

  useEffect(() => {
    if (user != null) {
      const db = getFirestore(firebase_app);
      const q = query(collection(db, 'Users', user.uid, 'CVs'));
      const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snapShot) => {
        const cvsData = snapShot.docs.map((doc) => {
          return { ...doc.data(), uid: doc.id };
        });
        setUserDocument((prevData) => {
          const mergedData = { ...prevData, cvs: cvsData };
          return mergedData;
        });
      });
      return () => unsubscribe();
    } else {
    }
  }, [user]);

  const setCvs = (data) => {
    setUserDocument((prevData) => {
      const mergedData = { ...prevData, cvs: data };
      return mergedData;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userDocument,
        userAdditionalInfo,
        setUserAdditionalInfo,
        bookmarks,
        loading,
        isBookmark,
        userDocumentLoaded,
        userDocumentLoading,
        removeToken,
        setCvs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
